
import React, { useState, useEffect, useRef } from "react";
import { Page, Grid, Card, Form, Button } from "tabler-react";
import PasswordInput from "../components/PasswordInput";
import SiteWrapper from "../SiteWrapper";

import * as userService from "../store/services/userServices";
import * as measureService from "../store/services/measureServices";



export default function MyAccount(props) {

    const [user, setUser] = useState((localStorage.getItem("user") && JSON.parse(localStorage.getItem("user"))) || {});

    const [ loading, setLoading ] = useState(false);
    const [ id ] = useState( user?.id );
    const [ password, setPassword ] = useState("password");
    const [ pwsConfirm, setPwsConfirm ] = useState("");
    const [ firstName, setFirstName ] = useState(user?.name || "");
    const [ lastName, setLastName ] = useState(user?.last_name || "");
    const [ email, setEmail ] = useState( user?.email || "" );
    const [ errors, setErrors ] = useState({});
    const [ saveSuccess, setSaveSuccess ] = useState(false);
    const [ isEditing, setIsEditing ] = useState(false);

    // Physical details
    const [ heightFeet, setHeightFeet ] = useState(user?.height_feet || "");
    const [ heightInches, setHeightInches ] = useState(user?.height_inches || "");
    const [ weight, setWeight ] = useState(user?.weight || "");
    const [ condition, setCondition ] = useState(user?.condition || "");
    const [ detailsEditing, setDetailsEditing ] = useState(false);
    const [ detailsSuccess, setDetailsSuccess ] = useState(false);
    const [ detailsErrors, setDetailsErrors ] = useState({});

    // Dealer
    const [ dealers, setDealers ] = useState([]);
    const [ selectedDealer, setSelectedDealer ] = useState(user?.dealer?.id || user?.dealer || "");
    const [ dealerSaving, setDealerSaving ] = useState(false);
    const [ dealerSuccess, setDealerSuccess ] = useState(false);
    const [ dealerError, setDealerError ] = useState("");
    const [ dealerSearch, setDealerSearch ] = useState("");
    const [ dealerDropdownOpen, setDealerDropdownOpen ] = useState(false);
    const dealerRef = useRef(null);

    // Measures
    const [ measures, setMeasures ] = useState([]);

    const isCustomer = user?.type === "Customer";

    useEffect(() => {
        if (isCustomer) {
            userService.getDetails(id)
                .then((res) => {
                    const data = JSON.parse(res.data)?.data;
                    setHeightFeet(data?.height_feet || "");
                    setHeightInches(data?.height_inches || "");
                    setWeight(data?.weight || "");
                    setCondition(data?.condition || "");
                })
                .catch(() => {});

            userService.getDealers()
                .then((res) => {
                    const dealerList = res.data?.data || [];
                    setDealers(dealerList);
                    const currentDealerId = user?.dealer?.id || user?.dealer;
                    if (currentDealerId) {
                        const d = dealerList.find(x => String(x.id) === String(currentDealerId));
                        if (d) setDealerSearch(`${String(d.id).padStart(4, '0')} — ${d.name} ${d.last_name}`);
                    }
                })
                .catch(() => {});

            measureService.getMyMeasures()
                .then((res) => {
                    const data = JSON.parse(res.data);
                    setMeasures(data?.data || []);
                })
                .catch(() => {});
        }
    }, [isCustomer, id, user.dealer]);

    const saveChanges = () => {
        setLoading(true);
        setErrors({});

        userService
            .updateAccount(id, { password: password, password_confirmation: pwsConfirm, firstName: firstName, lastName: lastName, email: email })
            .then((res) => {
                const data = JSON.parse(res.data);

                setFirstName(data.data.name || "");
                setLastName(data.data.last_name || "");
                setEmail(data.data.email || "");

                setSaveSuccess(true);
                setLoading(false);
            })
            .catch((err) => {
                setErrors(err?.response?.data?.errors || {});
                setLoading(false);
            });
    }

    const saveDetails = () => {
        setLoading(true);
        setDetailsErrors({});

        const heightCm = heightFeet || heightInches
            ? (parseFloat(heightFeet || 0) * 30.48 + parseFloat(heightInches || 0) * 2.54).toFixed(1)
            : undefined;

        userService
            .updateDetails({
                id,
                height: heightCm,
                height_feet: heightFeet,
                height_inches: heightInches,
                weight,
                condition,
            })
            .then(() => {
                const updatedUser = { ...user, height_feet: heightFeet, height_inches: heightInches, weight, condition };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setUser(updatedUser);
                setDetailsSuccess(true);
                setDetailsEditing(false);
                setLoading(false);
            })
            .catch((err) => {
                setDetailsErrors(err?.response?.data?.errors || {});
                setLoading(false);
            });
    }

    const saveDealer = () => {
        if (!selectedDealer) return;
        setDealerSaving(true);
        setDealerError("");
        setDealerSuccess(false);

        userService.changeDealer(selectedDealer)
            .then(() => {
                const dealerObj = dealers.find(d => String(d.id) === String(selectedDealer));
                const updatedUser = { ...user, dealer: dealerObj || selectedDealer };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setUser(updatedUser);
                setDealerSuccess(true);
                setDealerSaving(false);
            })
            .catch((err) => {
                setDealerError((err?.response?.data?.message) || "Failed to update dealer.");
                setDealerSaving(false);
            });
    }

    const removeDealer = () => {
        setDealerSaving(true);
        setDealerError("");
        setDealerSuccess(false);

        userService.changeDealer(0)
            .then(() => {
                const updatedUser = { ...user, dealer: 0 };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setUser(updatedUser);
                setSelectedDealer("");
                setDealerSearch("");
                setDealerSuccess(false);
                setDealerSaving(false);
            })
            .catch((err) => {
                setDealerError((err?.response?.data?.message) || "Failed to remove dealer.");
                setDealerSaving(false);
            });
    }

    const getMeasureLabel = (measure) => {
        const posture = measure?.posture || "";
        if (posture === "sit_side") return "Sit Side";
        if (posture === "sit_front") return "Sit Front";
        if (posture === "stand_side") return "Stand Side";
        if (posture === "stand_front") return "Stand Front";
        return posture || "—";
    }


    return (
        <SiteWrapper loading={loading}>
            <Page.Content>
                <div className="page-header d-flex justify-content-between">
                    <h1 className="page-title">My Account</h1>
                </div>

                <Grid.Row className="mb-4">
                    {/* Account Details */}
                    <Grid.Col md={isCustomer ? 4 : 12} width={12} className="mb-4 mb-lg-0">
                        <Card className="h-100">
                            <Card.Header className="d-flex justify-content-between">
                                <Card.Title>Account Details — ID {String(id).padStart(4, '0')}</Card.Title>
                                <Button
                                    color="secondary"
                                    onClick={() => {
                                        setPassword(isEditing ? "password" : "");
                                        setIsEditing(!isEditing);
                                        setSaveSuccess(false);
                                    }}
                                >
                                    {isEditing ? "Cancel" : "Edit"}
                                </Button>
                            </Card.Header>

                            <Card.Body>
                                {saveSuccess ? (
                                    <div>
                                        <h1 className="page-header-title">Saved</h1>
                                        <label className="text-secondary">Account successfully updated.</label><br />
                                        <Button
                                            color=" btn-mm d-flex justify-content-center align-items-center mt-3"
                                            onClick={() => { setPassword(""); setSaveSuccess(false); }}
                                        >
                                            Back To My Account
                                        </Button>
                                    </div>
                                ) : (
                                    <Grid.Row>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group>
                                                <Form.Input
                                                    placeholder="First Name"
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    value={firstName}
                                                    className="input-normal"
                                                    feedback={errors?.firstName?.[0]}
                                                    invalid={errors?.firstName}
                                                    readOnly={!isEditing}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group>
                                                <Form.Input
                                                    placeholder="Last Name"
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    value={lastName}
                                                    className="input-normal"
                                                    feedback={errors?.lastName?.[0]}
                                                    invalid={errors?.lastName}
                                                    readOnly={!isEditing}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group>
                                                <Form.Input
                                                    placeholder="Email Address"
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    value={email}
                                                    className="input-normal"
                                                    feedback={errors?.email?.[0]}
                                                    invalid={errors?.email}
                                                    readOnly={!isEditing}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group>
                                                <PasswordInput
                                                    placeholder="Password"
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    value={password}
                                                    className="form-control input-normal"
                                                    readOnly={!isEditing}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        {isEditing && (
                                            <>
                                                <Grid.Col width={12}>
                                                    <Form.Group>
                                                        <PasswordInput
                                                            placeholder="Confirm new password"
                                                            onChange={(e) => setPwsConfirm(e.target.value)}
                                                            value={pwsConfirm}
                                                            className="form-control input-normal"
                                                        />
                                                    </Form.Group>
                                                </Grid.Col>
                                                <Grid.Col width={12}>
                                                    <Button className="btn-mm d-flex justify-content-center align-items-center" onClick={saveChanges}>
                                                        Save Changes
                                                    </Button>
                                                </Grid.Col>
                                            </>
                                        )}
                                    </Grid.Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Grid.Col>

                    {/* Physical Details — customers only */}
                    {isCustomer && (
                        <Grid.Col md={4} width={12} className="mb-4 mb-lg-0">
                            <Card className="h-100">
                                <Card.Header className="d-flex justify-content-between">
                                    <Card.Title>Physical Details</Card.Title>
                                    <Button
                                        color="secondary"
                                        onClick={() => {
                                            setDetailsEditing(!detailsEditing);
                                            setDetailsSuccess(false);
                                        }}
                                    >
                                        {detailsEditing ? "Cancel" : "Edit"}
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    <Grid.Row>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group label="Height (feet)">
                                                <Form.Input
                                                    placeholder="e.g. 5"
                                                    onChange={(e) => setHeightFeet(e.target.value)}
                                                    value={heightFeet}
                                                    className="input-normal"
                                                    readOnly={!detailsEditing}
                                                    feedback={detailsErrors?.height_feet?.[0]}
                                                    invalid={detailsErrors?.height_feet}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group label="Height (inches)">
                                                <Form.Input
                                                    placeholder="e.g. 9"
                                                    onChange={(e) => setHeightInches(e.target.value)}
                                                    value={heightInches}
                                                    className="input-normal"
                                                    readOnly={!detailsEditing}
                                                    feedback={detailsErrors?.height_inches?.[0]}
                                                    invalid={detailsErrors?.height_inches}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group label="Weight (kg)">
                                                <Form.Input
                                                    placeholder="e.g. 75"
                                                    onChange={(e) => setWeight(e.target.value)}
                                                    value={weight}
                                                    className="input-normal"
                                                    readOnly={!detailsEditing}
                                                    feedback={detailsErrors?.weight?.[0]}
                                                    invalid={detailsErrors?.weight}
                                                />
                                            </Form.Group>
                                        </Grid.Col>
                                        <Grid.Col md={6} width={12}>
                                            <Form.Group label="Condition">
                                                <Form.Input
                                                    placeholder="e.g. Arthritis"
                                                    onChange={(e) => setCondition(e.target.value)}
                                                    value={condition}
                                                    className="input-normal"
                                                    readOnly={!detailsEditing}
                                                    feedback={detailsErrors?.condition?.[0]}
                                                    invalid={detailsErrors?.condition}
                                                />
                                            </Form.Group>
                                        </Grid.Col>

                                        {detailsSuccess && (
                                            <Grid.Col width={12}>
                                                <div style={{ color: "#2B9A99", fontSize: 14, fontWeight: 600 }}>Physical details saved.</div>
                                            </Grid.Col>
                                        )}

                                        {detailsEditing && (
                                            <Grid.Col width={12}>
                                                <Button className="btn-mm d-flex justify-content-center align-items-center" onClick={saveDetails}>
                                                    Save Details
                                                </Button>
                                            </Grid.Col>
                                        )}
                                    </Grid.Row>
                                </Card.Body>
                            </Card>
                        </Grid.Col>
                    )}

                    {/* Change Dealer — customers only */}
                    {isCustomer && (
                        <Grid.Col md={4} width={12} className="mb-4 mb-lg-0">
                            <Card className="h-100">
                                <Card.Header>
                                    <Card.Title>Your Dealer</Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <Grid.Row>
                                        <Grid.Col width={12}>
                                            <Form.Group label="Select Dealer">
                                                <div ref={dealerRef} style={{ position: 'relative' }}>
                                                    <div
                                                        onClick={() => { setDealerSearch(""); setDealerDropdownOpen(o => !o); }}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '6px 12px',
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: 4,
                                                            background: '#fff',
                                                            cursor: 'pointer',
                                                            fontSize: 14,
                                                            color: selectedDealer ? '#333' : '#999',
                                                            minHeight: 36,
                                                        }}
                                                    >
                                                        <span>
                                                            {selectedDealer
                                                                ? (() => { const d = dealers.find(x => String(x.id) === String(selectedDealer)); return d ? `${String(d.id).padStart(4, '0')} — ${d.name} ${d.last_name}` : '— Choose a dealer —'; })()
                                                                : '— Choose a dealer —'}
                                                        </span>
                                                        <span style={{ fontSize: 10, color: '#999' }}>▼</span>
                                                    </div>
                                                    {dealerDropdownOpen && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                background: '#fff',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: 4,
                                                                zIndex: 999,
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                            }}
                                                            onMouseDown={e => e.preventDefault()}
                                                        >
                                                            <div style={{ padding: '8px 8px 4px' }}>
                                                                <input
                                                                    autoFocus
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search by name or ID..."
                                                                    value={dealerSearch}
                                                                    onChange={e => setDealerSearch(e.target.value)}
                                                                    onBlur={() => setTimeout(() => setDealerDropdownOpen(false), 150)}
                                                                />
                                                            </div>
                                                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                                {dealers.filter(d => {
                                                                    const q = dealerSearch.toLowerCase();
                                                                    return !q || `${d.name} ${d.last_name}`.toLowerCase().includes(q) || String(d.id).padStart(4, '0').includes(q);
                                                                }).map(d => (
                                                                    <div
                                                                        key={d.id}
                                                                        onMouseDown={() => {
                                                                            setSelectedDealer(String(d.id));
                                                                            setDealerSearch("");
                                                                            setDealerDropdownOpen(false);
                                                                            setDealerSuccess(false);
                                                                            setDealerError("");
                                                                        }}
                                                                        style={{
                                                                            padding: '8px 12px',
                                                                            cursor: 'pointer',
                                                                            background: String(selectedDealer) === String(d.id) ? '#e8f5f5' : 'transparent',
                                                                            fontSize: 14,
                                                                        }}
                                                                        onMouseEnter={e => e.currentTarget.style.background = '#f0fafa'}
                                                                        onMouseLeave={e => e.currentTarget.style.background = String(selectedDealer) === String(d.id) ? '#e8f5f5' : 'transparent'}
                                                                    >
                                                                        <span style={{ color: '#2B9A99', fontWeight: 600, marginRight: 8 }}>{String(d.id).padStart(4, '0')}</span>
                                                                        {d.name} {d.last_name}
                                                                    </div>
                                                                ))}
                                                                {dealers.filter(d => {
                                                                    const q = dealerSearch.toLowerCase();
                                                                    return !q || `${d.name} ${d.last_name}`.toLowerCase().includes(q) || String(d.id).padStart(4, '0').includes(q);
                                                                }).length === 0 && (
                                                                    <div style={{ padding: '8px 12px', color: '#999', fontSize: 14 }}>No dealers found</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Form.Group>
                                        </Grid.Col>

                                        {selectedDealer && (
                                            <Grid.Col width={12}>
                                                <Button
                                                    className="btn-mm d-flex justify-content-center align-items-center"
                                                    onClick={saveDealer}
                                                    disabled={dealerSaving}
                                                >
                                                    {dealerSaving ? "Saving..." : "Save Dealer"}
                                                </Button>
                                            </Grid.Col>
                                        )}

                                        {!!(user?.dealer?.id ?? (user?.dealer || undefined)) && (
                                            <Grid.Col width={12} className="mt-2">
                                                <Button
                                                    color="danger"
                                                    outline
                                                    className="d-flex justify-content-center align-items-center w-100"
                                                    onClick={removeDealer}
                                                    disabled={dealerSaving}
                                                >
                                                    {dealerSaving ? "Removing..." : "Remove Dealer"}
                                                </Button>
                                            </Grid.Col>
                                        )}

                                        {dealerSuccess && (
                                            <Grid.Col width={12} className="mt-2">
                                                <div style={{ color: "#2B9A99", fontSize: 14, fontWeight: 600 }}>Dealer updated successfully.</div>
                                            </Grid.Col>
                                        )}

                                        {dealerError && (
                                            <Grid.Col width={12} className="mt-2">
                                                <div style={{ color: "#cd201f", fontSize: 14 }}>{dealerError}</div>
                                            </Grid.Col>
                                        )}
                                    </Grid.Row>
                                </Card.Body>
                            </Card>
                        </Grid.Col>
                    )}

                </Grid.Row>

                {/* Previous Measures — customers only */}
                {isCustomer && (
                    <Grid.Row>
                        <Grid.Col width={12}>
                            <Card>
                                <Card.Header>
                                    <Card.Title>Previous Measures</Card.Title>
                                </Card.Header>
                                <Card.Body style={{ padding: 0 }}>
                                    {measures.length === 0 ? (
                                        <div style={{ padding: "32px 24px", textAlign: "center", color: "#B3B3B3" }}>
                                            No measures recorded yet.
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: "auto" }}>
                                            <table className="table card-table table-vcenter">
                                                <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Date</th>
                                                        <th>Product</th>
                                                        <th>Pose</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {measures.map((m) => (
                                                        <tr key={m.id}>
                                                            <td>{m.id}</td>
                                                            <td style={{ whiteSpace: "nowrap" }}>
                                                                {m.date ? new Date(m.date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                                            </td>
                                                            <td style={{ whiteSpace: "nowrap" }}>{m.product || "—"}</td>
                                                            <td>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "2px 10px",
                                                                    borderRadius: 12,
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    backgroundColor: "#f0fafa",
                                                                    color: "#2B9A99",
                                                                }}>
                                                                    {getMeasureLabel(m)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Grid.Col>
                    </Grid.Row>
                )}
            </Page.Content>
        </SiteWrapper>
    )
}
